import { useState, useRef, useEffect, Fragment } from "react";
import { createPortal } from "react-dom";

/* ─── Markdown Renderer ──────────────────────────────────────── */
function renderMarkdown(text: string): React.ReactNode {
  // Split into paragraphs on double newline, then process each line
  const paragraphs = text.split(/\n\n+/);
  return paragraphs.map((para, pi) => {
    const lines = para.split("\n");
    const nodes = lines.map((line, li) => {
      const parts = parseInline(line);
      return (
        <Fragment key={li}>
          {parts}
          {li < lines.length - 1 && <br />}
        </Fragment>
      );
    });
    return <p key={pi} style={{ margin: pi === 0 ? "0" : "0.5rem 0 0" }}>{nodes}</p>;
  });
}

function parseInline(text: string): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  // Match **bold**, *italic*, [label](url), or bare https?:// URLs
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|\[([^\]]+)\]\((https?:\/\/[^\)]+)\)|(https?:\/\/[^\s]+)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) result.push(text.slice(last, match.index));
    if (match[1])      result.push(<strong key={match.index}>{match[2]}</strong>);
    else if (match[3]) result.push(<em key={match.index}>{match[4]}</em>);
    else if (match[5]) result.push(<a key={match.index} href={match[6]} target="_blank" rel="noreferrer" style={{ color: "#0d7680", wordBreak: "break-all" }}>{match[5]}</a>);
    else if (match[7]) result.push(<a key={match.index} href={match[7]} target="_blank" rel="noreferrer" style={{ color: "#0d7680", wordBreak: "break-all" }}>{match[7]}</a>);
    last = match.index + match[0].length;
  }
  if (last < text.length) result.push(text.slice(last));
  return result;
}

/* ─── Types ──────────────────────────────────────────────────── */
interface Message {
  id: number;
  role: "user" | "assistant";
  text: string;
  showChips?: boolean;
}

/* ─── FAQ Data ───────────────────────────────────────────────── */
const QUICK_REPLIES = [
  { label: "What documents do I need?",                      key: "docs"        },
  { label: "Am I eligible?",                                 key: "eligibility" },
  { label: "Access Indiana vs Guest?",                       key: "comparison"  },
  { label: "How long does the application take?",            key: "duration"    },
];

const HARDCODED_RESPONSES: Record<string, string> = {
  "What documents do I need?":
    "To apply for BDS services, you'll need:\n\n" +
    "**Required:**\n" +
    "- Full legal name, Social Security Number, and date of birth\n" +
    "- Physical address and contact information (phone/email)\n" +
    "- Age at time of disability diagnosis\n" +
    "- Description of how the disability affects daily life\n\n" +
    "**If the applicant is a minor or has a guardian:**\n" +
    "- Guardian's full name, relationship, address, and contact info\n\n" +
    "**Helpful but optional:** Medicaid number, gender, marital status, education level, race/ethnicity, preferred language\n\n" +
    "You may also need **State Form 54727 (Confirmation of Diagnosis)** — your doctor fills this out to verify the disability diagnosis.\n\n" +
    "All forms are available at: [BDS Forms](https://www.in.gov/fssa/ddars/resources/individuals/forms/)",

  "Am I eligible?":
    "BDS serves Indiana residents who have:\n\n" +
    "- An **intellectual or developmental disability (IDD)** diagnosed before age 22, **OR**\n" +
    "- A **traumatic brain injury (TBI)**\n\n" +
    "Eligibility is determined by BDS staff after reviewing your application. Applying does not guarantee services.\n\n" +
    "**Important:** Health and Wellness waivers and TBI waivers are handled by the **Area Agency on Aging**, not BDS. If you need those services, contact them instead.\n\n" +
    "For specific eligibility questions, contact your [local district office](https://www.in.gov/fssa/ddars/bds/bureau-of-disabilities-services/) or call **800-545-7763**.",

  "Access Indiana vs Guest?":
    "There are two ways to apply online:\n\n" +
    "**Access Indiana (Recommended):**\n" +
    "- Save your progress and return later\n" +
    "- Upload supporting documents\n" +
    "- Track your application status after submission\n\n" +
    "**Guest Application:**\n" +
    "- No account needed — start immediately\n" +
    "- Must complete in a single session\n" +
    "- 15-minute inactivity timeout (session will expire)\n" +
    "- Cannot upload documents (must mail or deliver them)\n" +
    "- Cannot track application status online\n\n" +
    "We recommend **Access Indiana** for most applicants — it's safer if you need to pause and come back.",

  "How long does the application take?":
    "The online application typically takes **15–30 minutes** if you have all your documents ready.\n\n" +
    "**After submitting:**\n" +
    "1. You'll receive a confirmation\n" +
    "2. BDS staff will review your application\n" +
    "3. A caseworker will contact you about next steps\n\n" +
    "Wait times vary depending on your district. For status updates, contact your [local district office](https://www.in.gov/fssa/ddars/bds/bureau-of-disabilities-services/) or email **BDS.Help@fssa.in.gov**.\n\n" +
    "**Tip:** If you apply as a Guest, you cannot check status online — you'll need to call your district office directly.",
};

const FALLBACK_REPLY =
  "Thank you for your question! For detailed assistance, please contact your local BDS district office or reach out to **BDS.Help@fssa.in.gov** or call **800-545-7763**.\n\n" +
  "You can also find helpful information at: [BDS Resources](https://www.in.gov/fssa/ddars/resources/)";

function getHardcodedReply(text: string): string {
  return HARDCODED_RESPONSES[text] ?? FALLBACK_REPLY;
}

/* ─── Typing Indicator ───────────────────────────────────────── */
function TypingIndicator() {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.5rem 0" }}>
      <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{
            width: "7px", height: "7px", borderRadius: "50%", background: "#0d7680",
            display: "inline-block",
            animation: "bds-bounce 1.2s ease-in-out infinite",
            animationDelay: `${i * 0.2}s`,
          }} />
        ))}
      </div>
    </div>
  );
}

/* ─── Chat Widget ────────────────────────────────────────────── */
export function ChatWidget() {
  const [isOpen, setIsOpen]       = useState(false);
  const [messages, setMessages]   = useState<Message[]>([
    { id: 1, role: "assistant", text: "Hi! I'm here to help you with your BDS application. I can answer questions about eligibility, required documents, or the application process.", showChips: true },
  ]);
  const [isTyping, setIsTyping]   = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [chipsUsed, setChipsUsed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  function sendMessage(text: string) {
    if (!text.trim()) return;
    const trimmed = text.trim();
    const userMsg: Message = { id: Date.now(), role: "user", text: trimmed };

    setMessages(prev => [...prev, userMsg]);
    setInputValue("");
    setChipsUsed(true);
    setIsTyping(true);

    // Hardcoded responses — no API call
    const replyText = getHardcodedReply(trimmed);
    setTimeout(() => {
      setIsTyping(false);
      const reply: Message = { id: Date.now() + 1, role: "assistant", text: replyText };
      setMessages(prev => [...prev, reply]);
    }, 600);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") sendMessage(inputValue);
  }

  const panelStyle: React.CSSProperties = {
    position: "fixed",
    bottom: "5.5rem",
    right: "1.5rem",
    width: "380px",
    height: "500px",
    zIndex: 1001,
    display: "flex",
    flexDirection: "column",
    borderRadius: "1.25rem",
    overflow: "hidden",
    boxShadow: "0 16px 48px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.1)",
    border: "1px solid rgba(13,118,128,0.15)",
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    transformOrigin: "bottom right",
    transform: isOpen ? "scale(1) translateY(0)" : "scale(0.85) translateY(24px)",
    opacity: isOpen ? 1 : 0,
    pointerEvents: isOpen ? "auto" : "none",
    transition: "transform 0.25s cubic-bezier(0.34,1.56,0.64,1), opacity 0.2s ease",
  };

  const widget = (
    <>
      {/* Keyframe injection */}
      <style>{`
        @keyframes bds-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40%            { transform: translateY(-6px); opacity: 1; }
        }
        @keyframes bds-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.5; }
          50%       { transform: scale(2.2); opacity: 0;   }
        }
        .bds-chip:hover {
          background: #0d7680 !important;
          color: #fff !important;
          border-color: #0d7680 !important;
        }
        .bds-fab:hover {
          transform: scale(1.1) !important;
        }
        .bds-send:hover:not(:disabled) {
          background: #0a5f67 !important;
        }
      `}</style>

      {/* ── Chat Panel ── */}
      <div style={panelStyle} role="dialog" aria-label="BDS Application Assistant" aria-modal="true">

        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #0d7680 0%, #1a3769 100%)",
          padding: "0.875rem 1rem",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
            <div style={{
              width: "36px", height: "36px", borderRadius: "50%",
              background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <div>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: "0.9rem", lineHeight: 1.2 }}>BDS Application Assistant</div>
              <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.7rem" }}>
                <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "50%", background: "#4ade80", marginRight: "4px", verticalAlign: "middle" }} />
                Online
              </div>
            </div>
          </div>
          <button type="button" onClick={() => setIsOpen(false)} aria-label="Close chat"
            style={{ background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", cursor: "pointer", width: "28px", height: "28px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}>
            ✕
          </button>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: "auto", padding: "1rem", background: "#f8fafc", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {messages.map((msg) => (
            <div key={msg.id}>
              {msg.role === "assistant" ? (
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                  <div style={{
                    width: "28px", height: "28px", borderRadius: "50%",
                    background: "linear-gradient(135deg, #0d7680, #1a3769)",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "2px",
                  }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div style={{ maxWidth: "80%" }}>
                    <div style={{
                      background: "#fff", borderRadius: "0 0.875rem 0.875rem 0.875rem",
                      padding: "0.625rem 0.875rem", fontSize: "0.825rem", color: "#1e293b",
                      lineHeight: 1.6, boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
                    }}>
                      {renderMarkdown(msg.text)}
                    </div>
                    {msg.showChips && !chipsUsed && (
                      <div style={{ marginTop: "0.625rem", display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                        {QUICK_REPLIES.map((chip) => (
                          <button key={chip.key} type="button" className="bds-chip"
                            onClick={() => sendMessage(chip.label)}
                            style={{
                              padding: "0.35rem 0.75rem", borderRadius: "99px",
                              border: "1.5px solid #0d7680", background: "#fff",
                              color: "#0d7680", fontSize: "0.75rem", fontWeight: 600,
                              cursor: "pointer", transition: "all 0.15s ease",
                              fontFamily: "inherit",
                            }}>
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <div style={{
                    background: "linear-gradient(135deg, #0d7680, #1a5f69)",
                    borderRadius: "0.875rem 0.875rem 0 0.875rem",
                    padding: "0.625rem 0.875rem", fontSize: "0.825rem", color: "#fff",
                    lineHeight: 1.6, maxWidth: "80%", boxShadow: "0 2px 6px rgba(13,118,128,0.25)",
                  }}>
                    {msg.text}
                  </div>
                </div>
              )}
            </div>
          ))}

          {isTyping && (
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <div style={{
                width: "28px", height: "28px", borderRadius: "50%",
                background: "linear-gradient(135deg, #0d7680, #1a3769)",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div style={{ background: "#fff", borderRadius: "0 0.875rem 0.875rem 0.875rem", padding: "0.5rem 0.875rem", boxShadow: "0 1px 3px rgba(0,0,0,0.07)" }}>
                <TypingIndicator />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={{ padding: "0.75rem", borderTop: "1px solid #e2e8f0", background: "#fff", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question..."
              aria-label="Type your message"
              style={{
                flex: 1, padding: "0.6rem 0.875rem", borderRadius: "0.625rem",
                border: "1.5px solid #cbd5e1", fontSize: "0.825rem", color: "#1e293b",
                outline: "none", fontFamily: "inherit", background: "#f8fafc",
                transition: "border-color 0.15s",
              }}
              onFocus={e => (e.target.style.borderColor = "#0d7680")}
              onBlur={e => (e.target.style.borderColor = "#cbd5e1")}
            />
            <button type="button" className="bds-send"
              onClick={() => sendMessage(inputValue)}
              disabled={!inputValue.trim()}
              aria-label="Send message"
              style={{
                width: "38px", height: "38px", borderRadius: "0.625rem", flexShrink: 0,
                background: inputValue.trim() ? "#0d7680" : "#e2e8f0",
                border: "none", cursor: inputValue.trim() ? "pointer" : "default",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "background 0.15s",
              }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={inputValue.trim() ? "#fff" : "#94a3b8"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
          <p style={{ fontSize: "0.67rem", color: "#94a3b8", margin: "0.4rem 0 0", textAlign: "center" }}>
            Responses are pre-written FAQs — not AI-generated
          </p>
        </div>
      </div>

      {/* ── Floating Action Button ── */}
      <button type="button" className="bds-fab"
        onClick={() => setIsOpen(prev => !prev)}
        aria-label={isOpen ? "Close Help Assistant" : "Open Help Assistant — answers questions about the BDS application"}
        aria-expanded={isOpen}
        style={{
          position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 1002,
          height: "52px",
          paddingLeft: isOpen ? "1.1rem" : "1rem",
          paddingRight: isOpen ? "1.1rem" : "1.25rem",
          borderRadius: "999px", border: "none",
          background: isOpen
            ? "linear-gradient(135deg, #475569 0%, #1e293b 100%)"
            : "linear-gradient(135deg, #0d7680 0%, #1a3769 100%)",
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
          boxShadow: "0 4px 20px rgba(13,118,128,0.45), 0 2px 8px rgba(0,0,0,0.2)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
          whiteSpace: "nowrap",
        }}>

        {isOpen ? (
          /* Close state — just an X with "Close" label */
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            <span style={{ color: "#fff", fontSize: "0.875rem", fontWeight: 600, fontFamily: "Inter, system-ui, sans-serif" }}>
              Close
            </span>
          </>
        ) : (
          /* Open state — icon + clear label */
          <>
            {/* Pulsing dot to signal "live" */}
            <span style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", flexShrink: 0 }}>
              <span style={{
                position: "absolute", width: "10px", height: "10px", borderRadius: "50%",
                background: "rgba(255,255,255,0.35)",
                animation: "bds-pulse 2s ease-in-out infinite",
              }} />
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: "relative", zIndex: 1 }}>
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </span>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.2 }}>
              <span style={{ color: "#fff", fontSize: "0.875rem", fontWeight: 700, fontFamily: "Inter, system-ui, sans-serif" }}>
                Have Questions?
              </span>
              <span style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.7rem", fontWeight: 400, fontFamily: "Inter, system-ui, sans-serif" }}>
                Ask our Application Assistant
              </span>
            </div>
          </>
        )}
      </button>
    </>
  );

  return createPortal(widget, document.body);
}
